import { supabase } from '../lib/supabase'

export interface LeanCustomsOrganization {
  id: string
  type: string
  name: string
  street: string
  postcode?: string
  place: string
  country_code: string
  vat_number?: string
  eori_number?: string
  coc_number?: string
  [key: string]: unknown
}

export interface LeanCustomsOrganizationsResponse {
  type: 'collection'
  results: LeanCustomsOrganization[]
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
  filters: unknown[]
}

export interface LeanCustomsContract {
  id: string
  agent_name: string
  agent_id: string
  requested_by_name?: string
  requested_by_id?: string
  status: string
  name?: string
  street?: string
  postcode?: string
  place?: string
  country_code?: string
  organization_id?: string
  organization_name?: string
  contract_type?: string
  duration_type?: string
  start_date?: string
  end_date?: string
  contract_reference?: string
  description?: string
  digitally_signed?: boolean
  signed_on?: string
  coc_number?: string
  eori_number?: string
  vat_number?: string
  created_at?: string
  updated_at?: string
  [key: string]: unknown
}

export interface LeanCustomsContractSigner {
  id?: string
  contract_id: string
  name: string
  position?: string
  email?: string
  identification_method: string
  document_type?: string
  document_number?: string
  signed_at?: string
  verified_at?: string
  status?: string
  status_reason?: string
  [key: string]: unknown
}

export interface LeanCustomsContractsResponse {
  type: 'collection'
  results: LeanCustomsContract[]
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
  filters: unknown[]
}

export interface LeanCustomsContractSignersResponse {
  type: 'collection'
  results: LeanCustomsContractSigner[]
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
  filters: unknown[]
}

export interface LeanCustomsContractSupportDocument {
  id: string
  contract_id: string
  filename: string
  content_type: string
  byte_size: number
  created_at?: string
  updated_at?: string
  [key: string]: unknown
}

export interface LeanCustomsContractSupportDocumentsResponse {
  type: 'collection'
  results: LeanCustomsContractSupportDocument[]
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
  filters: unknown[]
}

export interface LeanCustomsDeclaration {
  id: string
  uid: string
  reference?: string
  mrn?: string
  lrn?: string
  ticket_number?: number
  ticket_sequence_number?: number
  declaration_type?: string
  declaration_type_additional?: string
  dms_status?: string
  date?: string
  acceptance_date?: string
  clearance_date?: string
  release_date?: string
  agent_name?: string
  agent_id?: string
  declarant_name?: string
  declarant_id?: string
  importer_name?: string
  importer_id?: string
  exporter_name?: string
  exporter_id?: string
  buyer_name?: string
  buyer_id?: string
  seller_name?: string
  seller_id?: string
  forwarder_name?: string
  contract_id?: string
  contract_status?: string
  organization_id?: string
  created_at?: string
  updated_at?: string
  [key: string]: unknown
}

export interface LeanCustomsOrdersListResponse {
  type: 'collection'
  results: Array<Record<string, unknown> & { id?: string }>
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
  filters?: unknown[]
}

export interface LeanCustomsDeclarationsResponse {
  type: 'collection'
  results: LeanCustomsDeclaration[]
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
  filters: unknown[]
}

export interface LeanCustomsInvoiceLine {
  id: string
  description?: string
  quantity?: number
  unit_price?: string
  total_price_excl_tax?: string
  total_price_incl_tax?: string
  [key: string]: unknown
}

export interface LeanCustomsInvoiceDocument {
  id: string
  filename?: string
  content_type?: string
  byte_size?: number
  [key: string]: unknown
}

export interface LeanCustomsInvoice {
  id: string
  credit_for_id?: string
  supplier: {
    id: string
    name: string
  }
  customer: {
    id: string
    name: string
  }
  supplier_reference: string
  customer_reference: string
  invoice_type?: string
  billing_type?: string
  status?: string
  invoice_date?: string
  invoice_number?: string
  invoice_lines: LeanCustomsInvoiceLine[]
  documents: LeanCustomsInvoiceDocument[]
  [key: string]: unknown
}

export interface LeanCustomsInvoicesResponse {
  type: 'collection'
  results: LeanCustomsInvoice[]
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
  filters: unknown[]
}

export interface LeanCustomsDeclarationItem {
  id: string
  uid: string
  sequence_number: number
  description: string
  goods_nomenclature_code?: string
  customs_value: string
  gross_weight: string
  net_weight: string
  package_count: number
  origin_country_code?: string
  procedure_current?: string
  procedure_current_description?: string
  procedure_previous?: string
  procedure_previous_description?: string
  procedure_special?: string
  procedure_special_description?: string
  preference_code?: string
  preference_description?: string
  invoice_amount?: number
  invoice_currency?: string
  additional_code?: string
  additional_code2?: string
  cus_code?: string
  national_additional_code?: string
  quota_order_number?: string
  supplementary_measurement_quantity?: string
  supplementary_measurement_unit?: string
  valuation_arrangement?: boolean
  valuation_condition?: boolean
  valuation_method_code?: string
  valuation_related?: boolean
  valuation_restriction?: boolean
  created_at?: string
  updated_at?: string
  certificates?: unknown[]
  duty_tax_fees?: unknown[]
  [key: string]: unknown
}

export interface LeanCustomsDeclarationItemsResponse {
  type: 'collection'
  results: LeanCustomsDeclarationItem[]
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
  filters: unknown[]
}

export interface LeanCustomsChangeLog {
  id: string
  resource_type: string
  resource_id: number
  user_name: string
  action: 'create' | 'update' | 'delete'
  attributes: Record<string, unknown>
  changes: Record<string, [string, string]>
  created_at: string
  [key: string]: unknown
}

export interface LeanCustomsChangeLogResponse {
  type: 'collection'
  results: LeanCustomsChangeLog[]
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
  filters: unknown[]
}

/** Declaration / customs correspondence messages (shape varies by API version). */
export interface LeanCustomsDeclarationMessage {
  id?: string
  type?: string
  created_at?: string
  updated_at?: string
  [key: string]: unknown
}

export interface LeanCustomsDeclarationMessagesResponse {
  type: 'collection'
  results: LeanCustomsDeclarationMessage[]
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
  filters: unknown[]
}

export interface LeanCustomsOrderDeclaration {
  order_id: string
  declaration_id: string
  reference: string
  ticket_number?: number | null
  ticket_sequence_number?: number | null
  package_type_code?: string | null
  total_gross_mass?: string | null
  total_package_count?: number | null
  [key: string]: unknown
}

export interface LeanCustomsDeclarationGroupResponse {
  type: 'collection'
  results: LeanCustomsOrderDeclaration[]
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
  filters: unknown[]
}

export interface LeanCustomsDeclarationDocument {
  id: string
  document_type: string
  filename: string
  content_type: string
  byte_size: number
  provisional: boolean
  created_at: string
  [key: string]: unknown
}

export interface LeanCustomsDeclarationDocumentsResponse {
  type: 'collection'
  results: LeanCustomsDeclarationDocument[]
  pagination: {
    page: number
    page_size: number
    next_page: number | null
    prev_page: number | null
    total_pages: number
    total_count: number
  }
  filters: Array<{
    key: string
    value: string | null
    description: string | null
  }>
}

export interface LeanCustomsAPIResponse {
  success: boolean
  data?:
    | LeanCustomsOrganizationsResponse
    | { type: 'record'; data: LeanCustomsOrganization }
    | LeanCustomsContractsResponse
    | LeanCustomsContractSignersResponse
    | LeanCustomsContractSupportDocumentsResponse
    | LeanCustomsDeclarationsResponse
  error?: string
}

export class LeanCustomsService {
  private static async callEdgeFunction(
    action: string,
    params?: Record<string, unknown>
  ): Promise<LeanCustomsAPIResponse> {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        return { success: false, error: 'Not authenticated' }
      }

      // Get Supabase URL from environment
      const supabaseUrl =
        (typeof process !== 'undefined' && process.env.VITE_SUPABASE_URL) ||
        import.meta.env?.VITE_SUPABASE_URL ||
        ''

      if (!supabaseUrl) {
        return { success: false, error: 'Supabase URL not configured' }
      }

      // Determine which edge function to call based on action
      let functionName: string
      if (
        action === 'create-order' ||
        action === 'sync-order-costs' ||
        action === 'upload-order-files' ||
        action === 'get-order' ||
        action === 'list-orders' ||
        action === 'list-shipments'
      ) {
        functionName = 'leancustoms-orders'
      } else if (
        action === 'create-declaration' ||
        action.startsWith('list-declarations') ||
        action === 'get-declaration' ||
        action.startsWith('get-declaration-') ||
        action.startsWith('list-declaration-invoices') ||
        action.startsWith('list-declaration-items') ||
        action.startsWith('list-declaration-documents') ||
        action.startsWith('list-declaration-messages') ||
        action.startsWith('download-declaration-document') ||
        action === 'get-declaration-group'
      ) {
        functionName = 'leancustoms-declarations'
      } else {
        functionName = 'leancustoms-management'
      }

      const url = new URL(
        `${supabaseUrl}/functions/v1/${functionName}`
      )
      url.searchParams.set('action', action)

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(params || {}),
      })

      const responseText = await response.text()
      let payload: LeanCustomsAPIResponse | null = null
      if (responseText) {
        try {
          const parsed = JSON.parse(responseText) as LeanCustomsAPIResponse
          if (parsed && typeof parsed === 'object' && 'success' in parsed) {
            payload = parsed
          }
        } catch {
          // non-JSON body
        }
      }

      if (payload) {
        return payload
      }

      if (!response.ok) {
        return {
          success: false,
          error: responseText ? `Request failed: ${responseText}` : `Request failed (${response.status})`,
        }
      }

      return { success: true }
    } catch (error) {
      console.error('LeanCustoms service error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  static async listOrganizations(params?: {
    page?: number
    page_size?: number
    q?: string
    coc_number?: string
    eori_number?: string
    country_code?: string
    company_id?: number // Required for non-admin users
  }): Promise<{
    success: boolean
    data?: LeanCustomsOrganizationsResponse
    error?: string
  }> {
    const result = await this.callEdgeFunction('list-organizations', params)
    return {
      success: result.success,
      data: result.data as LeanCustomsOrganizationsResponse | undefined,
      error: result.error,
    }
  }

  static async getOrganization(
    organizationId: string,
    companyId?: number // Required for non-admin users
  ): Promise<{
    success: boolean
    data?: LeanCustomsOrganization
    error?: string
  }> {
    const result = await this.callEdgeFunction('get-organization', {
      organization_id: organizationId,
      company_id: companyId,
    })

    if (result.success && result.data) {
      const data = result.data as { type: 'record'; data: LeanCustomsOrganization }
      return {
        success: true,
        data: data.data,
      }
    }

    return {
      success: false,
      error: result.error,
    }
  }

  static async listOrganizationContracts(
    organizationId: string,
    companyId?: number,
    params?: {
      page?: number
      page_size?: number
    }
  ): Promise<{
    success: boolean
    data?: LeanCustomsContractsResponse
    error?: string
  }> {
    const result = await this.callEdgeFunction('list-organization-contracts', {
      organization_id: organizationId,
      company_id: companyId,
      ...params,
    })
    return {
      success: result.success,
      data: result.data as LeanCustomsContractsResponse | undefined,
      error: result.error,
    }
  }

  static async listContractSigners(
    contractId: string,
    companyId?: number,
    params?: {
      page?: number
      page_size?: number
    }
  ): Promise<{
    success: boolean
    data?: LeanCustomsContractSignersResponse
    error?: string
  }> {
    const result = await this.callEdgeFunction('list-contract-signers', {
      contract_id: contractId,
      company_id: companyId,
      ...params,
    })
    return {
      success: result.success,
      data: result.data as LeanCustomsContractSignersResponse | undefined,
      error: result.error,
    }
  }

  static async getContractSigner(
    signerId: string,
    companyId?: number
  ): Promise<{
    success: boolean
    data?: LeanCustomsContractSigner
    error?: string
  }> {
    const result = await this.callEdgeFunction('get-contract-signer', {
      signer_id: signerId,
      company_id: companyId,
    })

    if (result.success && result.data) {
      const responseData = result.data as unknown
      if (
        typeof responseData === 'object' &&
        responseData !== null &&
        'type' in responseData &&
        'data' in responseData &&
        (responseData as { type: string }).type === 'record'
      ) {
        const typedData = responseData as {
          type: 'record'
          data: LeanCustomsContractSigner
        }
        return {
          success: true,
          data: typedData.data,
        }
      }
    }

    return {
      success: false,
      error: result.error,
    }
  }

  static async listContractSupportDocuments(
    contractId: string,
    companyId?: number
  ): Promise<{
    success: boolean
    data?: LeanCustomsContractSupportDocumentsResponse
    error?: string
  }> {
    const result = await this.callEdgeFunction('list-contract-support-documents', {
      contract_id: contractId,
      company_id: companyId,
    })
    return {
      success: result.success,
      data: result.data as LeanCustomsContractSupportDocumentsResponse | undefined,
      error: result.error,
    }
  }

  static async downloadContractSupportDocument(
    contractId: string,
    documentId: string,
    companyId?: number
  ): Promise<Blob> {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('Not authenticated')
    }

    const supabaseUrl =
      (typeof process !== 'undefined' && process.env.VITE_SUPABASE_URL) ||
      import.meta.env?.VITE_SUPABASE_URL ||
      ''

    if (!supabaseUrl) {
      throw new Error('Supabase URL not configured')
    }

    const url = new URL(
      `${supabaseUrl}/functions/v1/leancustoms-management`
    )
    url.searchParams.set('action', 'download-contract-support-document')

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        contract_id: contractId,
        document_id: documentId,
        company_id: companyId,
      }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Download failed: ${errorText}`)
    }

    return await response.blob()
  }

  static async downloadInvoiceDocument(
    invoiceId: string,
    documentId: string,
    companyId?: number
  ): Promise<Blob> {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('Not authenticated')
    }

    const supabaseUrl =
      (typeof process !== 'undefined' && process.env.VITE_SUPABASE_URL) ||
      import.meta.env?.VITE_SUPABASE_URL ||
      ''

    if (!supabaseUrl) {
      throw new Error('Supabase URL not configured')
    }

    const url = new URL(
      `${supabaseUrl}/functions/v1/leancustoms-declarations`
    )
    url.searchParams.set('action', 'download-invoice-document')

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        invoice_id: invoiceId,
        document_id: documentId,
        company_id: companyId,
      }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Download failed: ${errorText}`)
    }

    return await response.blob()
  }

  static async downloadContractSignerDocument(
    contractId: string,
    signerId: string,
    companyId?: number
  ): Promise<Blob> {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('Not authenticated')
    }

    const supabaseUrl =
      (typeof process !== 'undefined' && process.env.VITE_SUPABASE_URL) ||
      import.meta.env?.VITE_SUPABASE_URL ||
      ''

    if (!supabaseUrl) {
      throw new Error('Supabase URL not configured')
    }

    const url = new URL(
      `${supabaseUrl}/functions/v1/leancustoms-management`
    )
    url.searchParams.set('action', 'download-contract-signer-document')

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        contract_id: contractId,
        signer_id: signerId,
        company_id: companyId,
      }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Download failed: ${errorText}`)
    }

    return await response.blob()
  }

  static async listDeclarations(
    organizationId: string,
    companyId?: number,
    params?: {
      page?: number
      page_size?: number
      q?: string
      status?: string
      declaration_type?: string
      order_id?: string
    }
  ): Promise<{
    success: boolean
    data?: LeanCustomsDeclarationsResponse
    error?: string
  }> {
    const result = await this.callEdgeFunction('list-declarations', {
      organization_id: organizationId,
      company_id: companyId,
      ...params,
    })
    return {
      success: result.success,
      data: result.data as LeanCustomsDeclarationsResponse | undefined,
      error: result.error,
    }
  }

  static async getDeclaration(
    declarationId: string,
    companyId?: number
  ): Promise<{
    success: boolean
    data?: LeanCustomsDeclaration
    error?: string
  }> {
    const result = await this.callEdgeFunction('get-declaration', {
      declaration_id: declarationId,
      company_id: companyId,
    })
    return {
      success: result.success,
      data: result.data as LeanCustomsDeclaration | undefined,
      error: result.error,
    }
  }

  static async createOrder(
    companyId: number,
    orderPayload: Record<string, unknown>
  ): Promise<{ success: boolean; data?: { data?: { id?: string; shipment_id?: string } }; error?: string }> {
    const result = await this.callEdgeFunction('create-order', {
      company_id: companyId,
      ...orderPayload,
    })
    return {
      success: result.success,
      data: result.data as { data?: { id?: string; shipment_id?: string } } | undefined,
      error: result.error,
    }
  }

  static async syncOrderCosts(
    companyId: number,
    orderId: string,
    orderCosts: Array<{
      cost_type: string
      amount: number
      currency: string
      allocation_type?: string | null
    }>
  ): Promise<{
    success: boolean
    created?: number
    updated?: number
    failed?: Array<{ cost_type: string; error: string }>
    error?: string
  }> {
    const result = await this.callEdgeFunction('sync-order-costs', {
      company_id: companyId,
      order_id: orderId,
      order_costs_attributes: orderCosts,
    }) as {
      success: boolean
      created?: number
      updated?: number
      failed?: Array<{ cost_type: string; error: string }>
      error?: string
    }
    return {
      success: result.success,
      created: result.created,
      updated: result.updated,
      failed: result.failed,
      error: result.error,
    }
  }

  static async uploadOrderFiles(
    companyId: number,
    orderId: string,
    documents: Array<{ bucket: string; path: string }>,
    directFiles?: Array<{ filename: string; content_base64: string; mime_type?: string }>
  ): Promise<{ success: boolean; uploaded?: number; failed?: Array<{ path: string; error: string }>; error?: string }> {
    const result = await this.callEdgeFunction('upload-order-files', {
      company_id: companyId,
      order_id: orderId,
      documents,
      direct_files: directFiles,
    }) as { success: boolean; uploaded?: number; failed?: Array<{ path: string; error: string }>; error?: string }
    return {
      success: result.success,
      uploaded: result.uploaded,
      failed: result.failed,
      error: result.error,
    }
  }

  static async getOrder(
    orderId: string,
    companyId?: number
  ): Promise<{
    success: boolean
    data?: Record<string, unknown>
    error?: string
  }> {
    const result = await this.callEdgeFunction('get-order', {
      order_id: orderId,
      company_id: companyId,
    })
    return {
      success: result.success,
      data: result.data as Record<string, unknown> | undefined,
      error: result.error,
    }
  }

  static async listOrderShipments(
    orderId: string,
    companyId?: number
  ): Promise<{
    success: boolean
    data?: unknown
    error?: string
  }> {
    const result = await this.callEdgeFunction('list-shipments', {
      order_id: orderId,
      company_id: companyId,
    })
    return {
      success: result.success,
      data: result.data,
      error: result.error,
    }
  }

  static async listOrders(
    organizationId: string,
    companyId?: number,
    params?: {
      page?: number
      page_size?: number
      q?: string
      reference?: string
      status?: string
      order_type?: string
      transport_mode?: string
    }
  ): Promise<{
    success: boolean
    data?: LeanCustomsOrdersListResponse
    error?: string
  }> {
    const result = await this.callEdgeFunction('list-orders', {
      organization_id: organizationId,
      company_id: companyId,
      ...params,
    })
    return {
      success: result.success,
      data: result.data as LeanCustomsOrdersListResponse | undefined,
      error: result.error,
    }
  }

  static async createDeclaration(
    companyId: number,
    declarationPayload: Record<string, unknown>
  ): Promise<{ success: boolean; data?: { data?: { id?: string } }; error?: string }> {
    const result = await this.callEdgeFunction('create-declaration', {
      company_id: companyId,
      ...declarationPayload,
    })
    return {
      success: result.success,
      data: result.data as { data?: { id?: string } } | undefined,
      error: result.error,
    }
  }

  static async listDeclarationInvoices(
    declarationId: string,
    companyId?: number
  ): Promise<{
    success: boolean
    data?: LeanCustomsInvoicesResponse
    error?: string
  }> {
    const result = await this.callEdgeFunction('list-declaration-invoices', {
      declaration_id: declarationId,
      company_id: companyId,
    })
    return {
      success: result.success,
      data: result.data as LeanCustomsInvoicesResponse | undefined,
      error: result.error,
    }
  }

  static async listDeclarationItems(
    declarationId: string,
    companyId?: number
  ): Promise<{
    success: boolean
    data?: LeanCustomsDeclarationItemsResponse
    error?: string
  }> {
    const result = await this.callEdgeFunction('list-declaration-items', {
      declaration_id: declarationId,
      company_id: companyId,
    })
    return {
      success: result.success,
      data: result.data as LeanCustomsDeclarationItemsResponse | undefined,
      error: result.error,
    }
  }

  static async getDeclarationChangelog(
    declarationId: string,
    companyId?: number
  ): Promise<{
    success: boolean
    data?: LeanCustomsChangeLogResponse
    error?: string
  }> {
    const result = await this.callEdgeFunction('get-declaration-changelog', {
      declaration_id: declarationId,
      company_id: companyId,
    })
    return {
      success: result.success,
      data: result.data as LeanCustomsChangeLogResponse | undefined,
      error: result.error,
    }
  }

  static async listDeclarationMessages(
    declarationId: string,
    companyId?: number
  ): Promise<{
    success: boolean
    data?: LeanCustomsDeclarationMessagesResponse
    error?: string
  }> {
    const result = await this.callEdgeFunction('list-declaration-messages', {
      declaration_id: declarationId,
      company_id: companyId,
    })
    return {
      success: result.success,
      data: result.data as LeanCustomsDeclarationMessagesResponse | undefined,
      error: result.error,
    }
  }

  static async getDeclarationGroup(
    declarationId: string,
    companyId?: number
  ): Promise<{
    success: boolean
    data?: LeanCustomsDeclarationGroupResponse
    error?: string
  }> {
    const result = await this.callEdgeFunction('get-declaration-group', {
      declaration_id: declarationId,
      company_id: companyId,
    })
    return {
      success: result.success,
      data: result.data as LeanCustomsDeclarationGroupResponse | undefined,
      error: result.error,
    }
  }

  static async listDeclarationDocuments(
    declarationId: string,
    companyId?: number
  ): Promise<{
    success: boolean
    data?: LeanCustomsDeclarationDocumentsResponse
    error?: string
  }> {
    const result = await this.callEdgeFunction('list-declaration-documents', {
      declaration_id: declarationId,
      company_id: companyId,
    })
    return {
      success: result.success,
      data: result.data as LeanCustomsDeclarationDocumentsResponse | undefined,
      error: result.error,
    }
  }

  static async downloadDeclarationDocument(
    declarationId: string,
    documentId: string,
    companyId?: number
  ): Promise<Blob> {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('Not authenticated')
    }

    const supabaseUrl =
      (typeof process !== 'undefined' && process.env.VITE_SUPABASE_URL) ||
      import.meta.env?.VITE_SUPABASE_URL ||
      ''

    if (!supabaseUrl) {
      throw new Error('Supabase URL not configured')
    }

    const url = new URL(
      `${supabaseUrl}/functions/v1/leancustoms-declarations`
    )
    url.searchParams.set('action', 'download-declaration-document')

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        declaration_id: declarationId,
        document_id: documentId,
        company_id: companyId,
      }),
    })

    if (!response.ok) {
      let errorText = ''
      try {
        const errorData = await response.json()
        errorText = errorData.error || JSON.stringify(errorData)
      } catch {
        errorText = await response.text()
      }
      
      // Provide user-friendly error message
      if (response.status === 404) {
        throw new Error(`Document not found. The document may have been removed or the ID is incorrect.`)
      }
      
      throw new Error(`Download failed (${response.status}): ${errorText}`)
    }

    return await response.blob()
  }

  static async createContract(
    contractData: Record<string, unknown>,
    companyId: number
  ): Promise<{
    success: boolean
    data?: LeanCustomsContract & { signup_url?: string }
    error?: string
  }> {
    const result = await this.callEdgeFunction('create-contract', {
      contract: contractData,
      company_id: companyId,
    })

    if (result.success && result.data) {
      const responseData = result.data as unknown
      if (
        typeof responseData === 'object' &&
        responseData !== null &&
        'type' in responseData &&
        'data' in responseData &&
        (responseData as { type: string }).type === 'record'
      ) {
        const typedData = responseData as {
          type: 'record'
          data: LeanCustomsContract & { signup_url?: string }
        }
        return {
          success: true,
          data: typedData.data,
        }
      }
      // Handle case where data is directly the contract
      return {
        success: true,
        data: responseData as LeanCustomsContract & { signup_url?: string },
      }
    }

    return {
      success: false,
      error: result.error,
    }
  }

  static async getContractSignupUrl(
    contractId: string,
    companyId: number
  ): Promise<{
    success: boolean
    data?: { signup_url: string }
    error?: string
  }> {
    const result = await this.callEdgeFunction('get-contract-signup-url', {
      contract_id: contractId,
      company_id: companyId,
    })

    if (result.success && result.data) {
      return {
        success: true,
        data: result.data as { signup_url: string },
      }
    }

    return {
      success: false,
      error: result.error,
    }
  }

  static async getContract(
    contractId: string,
    companyId: number
  ): Promise<{
    success: boolean
    data?: LeanCustomsContract
    error?: string
  }> {
    const result = await this.callEdgeFunction('get-contract', {
      contract_id: contractId,
      company_id: companyId,
    })

    if (result.success && result.data) {
      const responseData = result.data as unknown
      if (
        typeof responseData === 'object' &&
        responseData !== null &&
        'type' in responseData &&
        'data' in responseData &&
        (responseData as { type: string }).type === 'record'
      ) {
        const typedData = responseData as {
          type: 'record'
          data: LeanCustomsContract
        }
        return {
          success: true,
          data: typedData.data,
        }
      }
      return {
        success: true,
        data: responseData as LeanCustomsContract,
      }
    }

    return {
      success: false,
      error: result.error,
    }
  }

  static async linkOrganization(
    organizationId: string,
    companyId: number,
    contractId?: string
  ): Promise<{
    success: boolean
    error?: string
  }> {
    const result = await this.callEdgeFunction('link-organization', {
      organization_id: organizationId,
      company_id: companyId,
      ...(contractId ? { contract_id: contractId } : {}),
    })

    return {
      success: result.success,
      error: result.error,
    }
  }
}

